import { useCustom, useCustomMutation } from '@refinedev/core';
import { Eye, MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { RECOVERY_LABEL, RECOVERY_VARIANT, formatRelative } from '@/lib/format';
import type { RecoveryCode } from '@/lib/types';
import { AssignCodeDialog } from './assign-code-dialog';
import { RevealCodeDialog } from './reveal-code-dialog';

interface Props {
  accountId: string;
}

interface CodesResponse {
  data: RecoveryCode[];
}

type ConfirmKind = 'unassign' | 'mark-used' | 'mark-lost';

interface ConfirmState {
  open: boolean;
  kind: ConfirmKind | null;
  codeIndex: number | null;
}

const CONFIRM_TEXTS: Record<ConfirmKind, { title: string; description: string; label: string; destructive: boolean }> = {
  unassign: {
    title: 'Desatribuir code',
    description: 'O code volta pra disponível e pode ser atribuído a outro cliente.',
    label: 'Desatribuir',
    destructive: false,
  },
  'mark-used': {
    title: 'Marcar como usado',
    description: 'Cliente confirmou que usou este code pra logar. Não é reversível.',
    label: 'Marcar usado',
    destructive: false,
  },
  'mark-lost': {
    title: 'Marcar como perdido',
    description: 'Code queimado/perdido. Não pode ser reaproveitado.',
    label: 'Marcar perdido',
    destructive: true,
  },
};

export function RecoveryCodesTab({ accountId }: Props) {
  const { data, isLoading, refetch } = useCustom<CodesResponse>({
    url: `/psn-accounts/${accountId}/recovery-codes`,
    method: 'get',
  });
  const { mutate, isLoading: mutating } = useCustomMutation();

  const [assignTarget, setAssignTarget] = useState<number | null>(null);
  const [revealTarget, setRevealTarget] = useState<number | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState>({ open: false, kind: null, codeIndex: null });
  const [registerPaste, setRegisterPaste] = useState('');

  const onRegister = () => {
    const lines = registerPaste
      .split(/[\n,;\s]+/)
      .map((c) => c.trim())
      .filter(Boolean);
    if (lines.length !== 10) {
      toast.error(`Esperado 10 recovery codes, recebido ${lines.length}. Cole 1 por linha.`);
      return;
    }
    mutate(
      {
        url: `/psn-accounts/${accountId}/recovery-codes/register`,
        method: 'post',
        values: { codes: lines },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          toast.success('10 recovery codes registrados.');
          setRegisterPaste('');
          refetch();
        },
        onError: (err) => {
          const code = (err as { errorCode?: string })?.errorCode;
          if (code === 'codes_already_registered') {
            toast.error('Esta conta já tem codes registrados. Recarregue a página.');
            refetch();
          } else if (code === 'duplicate_codes') {
            toast.error('Há codes repetidos na lista.');
          } else {
            toast.error('Falha ao registrar os codes.');
          }
        },
      },
    );
  };

  const codes = (data?.data?.data ?? []) as RecoveryCode[];

  const runAction = (kind: ConfirmKind, codeIndex: number) => {
    mutate(
      {
        url: `/psn-accounts/${accountId}/recovery-codes/${codeIndex}/${kind}`,
        method: 'post',
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          toast.success(`Code ${codeIndex} atualizado.`);
          setConfirm({ open: false, kind: null, codeIndex: null });
          refetch();
        },
        onError: () => toast.error('Falha na transição. Verifique o status atual.'),
      },
    );
  };

  if (isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  if (!codes.length) {
    return (
      <div className="space-y-4 rounded-md border bg-muted/30 p-6">
        <p className="text-sm text-muted-foreground">
          Esta conta ainda não tem recovery codes registrados. Cole os 10 codes do PSN abaixo
          (cada um fica encriptado individualmente).
        </p>
        <div className="space-y-2">
          <Label htmlFor="register-codes">Recovery codes (10)</Label>
          <Textarea
            id="register-codes"
            rows={6}
            placeholder={'1 por linha\nex:\nABCD-1234\nEFGH-5678\n...'}
            value={registerPaste}
            onChange={(e) => setRegisterPaste(e.target.value)}
            spellCheck={false}
            autoComplete="off"
            className="font-mono text-sm"
          />
        </div>
        <div className="flex justify-end">
          <Button onClick={onRegister} disabled={mutating || !registerPaste.trim()}>
            Registrar 10 codes
          </Button>
        </div>
      </div>
    );
  }

  const counts = codes.reduce<Record<string, number>>((acc, c) => {
    acc[c.status] = (acc[c.status] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
        <span>{codes.length} codes</span>
        {(Object.keys(counts) as Array<keyof typeof counts>).map((s) => (
          <span key={s} className="rounded bg-muted px-2 py-0.5">
            {RECOVERY_LABEL[s as keyof typeof RECOVERY_LABEL] ?? s}: {counts[s]}
          </span>
        ))}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">#</TableHead>
              <TableHead className="w-28">Status</TableHead>
              <TableHead>Referência</TableHead>
              <TableHead className="w-32">Atribuído</TableHead>
              <TableHead className="w-32">Usado</TableHead>
              <TableHead className="w-[260px] text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {codes.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-mono">{c.code_index}</TableCell>
                <TableCell>
                  <Badge variant={RECOVERY_VARIANT[c.status]}>{RECOVERY_LABEL[c.status]}</Badge>
                </TableCell>
                <TableCell className="max-w-xs truncate text-sm">
                  {c.assigned_reference ?? <span className="text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {formatRelative(c.assigned_at)}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {formatRelative(c.used_at)}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex flex-wrap justify-end gap-1">
                    {c.status === 'available' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setAssignTarget(c.code_index)}
                      >
                        Atribuir
                      </Button>
                    )}
                    {c.status === 'assigned' && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setConfirm({ open: true, kind: 'unassign', codeIndex: c.code_index })}
                        >
                          Desatribuir
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setConfirm({ open: true, kind: 'mark-used', codeIndex: c.code_index })}
                        >
                          Usado
                        </Button>
                      </>
                    )}
                    {(c.status === 'available' || c.status === 'assigned') && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setConfirm({ open: true, kind: 'mark-lost', codeIndex: c.code_index })}
                      >
                        Perdido
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setRevealTarget(c.code_index)}
                      title="Revelar code"
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AssignCodeDialog
        accountId={accountId}
        codeIndex={assignTarget}
        open={assignTarget !== null}
        onOpenChange={(o) => !o && setAssignTarget(null)}
        onAssigned={() => refetch()}
      />

      <RevealCodeDialog
        accountId={accountId}
        codeIndex={revealTarget}
        open={revealTarget !== null}
        onOpenChange={(o) => !o && setRevealTarget(null)}
      />

      <ConfirmDialog
        open={confirm.open}
        onOpenChange={(o) => !o && setConfirm({ open: false, kind: null, codeIndex: null })}
        title={confirm.kind ? CONFIRM_TEXTS[confirm.kind].title : ''}
        description={confirm.kind ? CONFIRM_TEXTS[confirm.kind].description : ''}
        confirmLabel={confirm.kind ? CONFIRM_TEXTS[confirm.kind].label : 'OK'}
        destructive={confirm.kind ? CONFIRM_TEXTS[confirm.kind].destructive : false}
        loading={mutating}
        onConfirm={() => {
          if (confirm.kind && confirm.codeIndex !== null) {
            runAction(confirm.kind, confirm.codeIndex);
          }
        }}
      />
    </div>
  );
}
